/**
 * Welle 8c-pre — Geräteauswahl & Fehlerklassifizierung (Berater).
 *
 * - enumerateDevices() für Kamera / Mikrofon / Lautsprecher
 * - zuletzt gewählte Quelle in localStorage merken (gilt für nächste Calls)
 * - getUserMedia-Fehler in verständliche deutsche Meldungen übersetzen
 */

export type DeviceKind = 'videoinput' | 'audioinput' | 'audiooutput';

export interface DeviceOption {
  deviceId: string;
  label: string;
}

const LS_KEYS: Record<DeviceKind, string> = {
  videoinput: 'bamoa_dev_video',
  audioinput: 'bamoa_dev_audio_in',
  audiooutput: 'bamoa_dev_audio_out',
};

export function getPreferredDevice(kind: DeviceKind): string | null {
  try {
    return localStorage.getItem(LS_KEYS[kind]);
  } catch {
    return null;
  }
}

export function setPreferredDevice(kind: DeviceKind, deviceId: string | null): void {
  try {
    if (deviceId) localStorage.setItem(LS_KEYS[kind], deviceId);
    else localStorage.removeItem(LS_KEYS[kind]);
  } catch {
    /* ignore */
  }
}

/** Welle 9 (pre85): Kamera-Capture-Auflösung als Präferenz (gilt für nächsten Call). */
export type ResolutionKey = '480' | '720' | '1080';
const RES_MAP: Record<ResolutionKey, { width: number; height: number; label: string }> = {
  '480': { width: 854, height: 480, label: '480p (bandbreitenschonend)' },
  '720': { width: 1280, height: 720, label: '720p (Standard)' },
  '1080': { width: 1920, height: 1080, label: '1080p (höchste Qualität)' },
};
export const RESOLUTION_OPTIONS = (Object.keys(RES_MAP) as ResolutionKey[]).map((k) => ({ key: k, ...RES_MAP[k] }));

export function getPreferredResolutionKey(): ResolutionKey {
  try {
    const v = localStorage.getItem('bamoa_dev_video_res') as ResolutionKey | null;
    return v && RES_MAP[v] ? v : '720';
  } catch { return '720'; }
}
export function setPreferredResolutionKey(key: ResolutionKey): void {
  try { localStorage.setItem('bamoa_dev_video_res', key); } catch { /* ignore */ }
}
export function getPreferredResolution(): { width: number; height: number } {
  const r = RES_MAP[getPreferredResolutionKey()];
  return { width: r.width, height: r.height };
}

/** Welle 9 (pre85): Wiedergabe-Lautstärke (0..1) als Präferenz. */
export function getPreferredVolume(): number {
  try {
    const v = parseFloat(localStorage.getItem('bamoa_dev_volume') || '');
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1;
  } catch { return 1; }
}
export function setPreferredVolume(v: number): void {
  try { localStorage.setItem('bamoa_dev_volume', String(Math.max(0, Math.min(1, v)))); } catch { /* ignore */ }
}

/** Geräte je Art auflisten. Labels sind erst nach erteilter Berechtigung gefüllt. */
export async function listDevices(): Promise<Record<DeviceKind, DeviceOption[]>> {
  const out: Record<DeviceKind, DeviceOption[]> = {
    videoinput: [],
    audioinput: [],
    audiooutput: [],
  };
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    let camN = 0, micN = 0, spkN = 0;
    for (const d of devices) {
      if (d.kind === 'videoinput') {
        out.videoinput.push({ deviceId: d.deviceId, label: d.label || `Kamera ${++camN}` });
      } else if (d.kind === 'audioinput') {
        out.audioinput.push({ deviceId: d.deviceId, label: d.label || `Mikrofon ${++micN}` });
      } else if (d.kind === 'audiooutput') {
        out.audiooutput.push({ deviceId: d.deviceId, label: d.label || `Lautsprecher ${++spkN}` });
      }
    }
  } catch (e) {
    console.warn('[devices] enumerateDevices fehlgeschlagen', e);
  }
  return out;
}

/** Ob Lautsprecher-Auswahl (setSinkId) im aktuellen Browser unterstützt wird (i.d.R. nur Chromium). */
export function speakerSelectionSupported(): boolean {
  try {
    return typeof (HTMLMediaElement.prototype as any).setSinkId === 'function';
  } catch {
    return false;
  }
}

/** getUserMedia-/Geräte-Fehler in eine verständliche Meldung übersetzen. */
export function describeMediaError(e: unknown): string {
  const err = e as { name?: string; message?: string };
  switch (err?.name) {
    case 'NotReadableError': // Firefox: "Failed to allocate videosource"
    case 'AbortError':
      return 'Kamera oder Mikrofon konnte nicht geöffnet werden — vermutlich wird das Gerät von einer anderen App oder einem anderen Tab benutzt. Bitte andere Programme schließen und neu laden.';
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Zugriff auf Kamera/Mikrofon wurde verweigert. Bitte die Berechtigung im Browser erlauben.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Kein passendes Kamera-/Mikrofon-Gerät gefunden. Bitte ein Gerät anschließen oder eine andere Quelle wählen.';
    default:
      return err?.message
        ? `Medienzugriff fehlgeschlagen: ${err.message}`
        : 'Medienzugriff fehlgeschlagen.';
  }
}
